'use client';

import { useState, useRef, useEffect } from 'react';
import { BookOpen, ExternalLink, HelpCircle, Mail, Upload, Zap, RefreshCw } from 'lucide-react';

const HOW_TO_USE_URL = 'https://app.taskloops.com/how-to-use';

const featureLinks = [
  { label: 'CSV Import Guide', icon: Upload, anchor: '#csv-import' },
  { label: 'Bulk Operations Guide', icon: Zap, anchor: '#bulk-operations' },
  { label: 'Recurring Tasks Guide', icon: RefreshCw, anchor: '#recurring-tasks' },
];

export function HelpMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const openLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-body)] transition-colors"
        aria-label="Help"
        aria-expanded={isOpen}
      >
        <HelpCircle className="w-5 h-5" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-64 rounded-lg border border-[var(--border-default)] bg-[var(--surface-card)] shadow-lg z-50 overflow-hidden">
          {/* Primary link */}
          <button
            onClick={() => openLink(HOW_TO_USE_URL)}
            className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium text-[var(--text-heading)] hover:bg-[var(--surface-hover)] transition-colors"
          >
            <BookOpen className="w-4 h-4 text-primary-600 shrink-0" />
            <span className="flex-1 text-left">How to Use TaskLoops</span>
            <ExternalLink className="w-3.5 h-3.5 text-[var(--text-muted)] shrink-0" />
          </button>

          {/* Separator */}
          <div className="h-px bg-[var(--border-default)]" />

          {/* Feature-specific guides */}
          <div className="py-1">
            {featureLinks.map(({ label, icon: Icon, anchor }) => (
              <button
                key={anchor}
                onClick={() => openLink(`${HOW_TO_USE_URL}${anchor}`)}
                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-[var(--text-body)] hover:bg-[var(--surface-hover)] transition-colors"
              >
                <Icon className="w-4 h-4 text-[var(--text-secondary)] shrink-0" />
                <span className="flex-1 text-left">{label}</span>
              </button>
            ))}
          </div>

          {/* Separator */}
          <div className="h-px bg-[var(--border-default)]" />

          {/* Contact */}
          <a
            href="mailto:hello@taskloops.com"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 w-full px-4 py-3 text-sm text-[var(--text-body)] hover:bg-[var(--surface-hover)] transition-colors"
          >
            <Mail className="w-4 h-4 text-[var(--text-secondary)] shrink-0" />
            <span className="flex-1 text-left">Contact Support</span>
          </a>
        </div>
      )}
    </div>
  );
}
